import React, { FC } from "react";
//#region redux
import { useDispatch } from "react-redux";
import { displayDialog, displayErrorMessage } from "store/actions/utilActions";
//#endregion
//#region types
import { AccumulatorsSummaryItem, WintDeviationRowFooterProps } from "models/propTypes/wintSalaryPropTypes";
import { MoreActionMenuItem } from "models/propTypes/menuPropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { AdaptiveDetailTableView, Icon, MenuButton, RoundButton } from "components/common";
import { useWintDeviationCreateDialog } from "components/wintSalary";
import useSalaryChangeActions from "../useSalaryChangeActions";
import UserPayoutDetails from "./userPayoutDetails";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import DateHelper from "utils/helpers/dateHelper";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

const WintDeviationRowFooter: FC<WintDeviationRowFooterProps> = (props) => {
  const {
    person,
    regularPayoutAmount,
    taxAmount,
    taxTable,
    accumulators,
    hideUserPayoutDetails = false,
    reloadHandler,
    hideMoreActions = false,
    actionType,
    startDate,
    earlyPayoutsAmount,
    regularPayoutDate,
    payouts,
    unscheduledPayoutLabel,
  } = props;

  const dispatch = useDispatch();

  const createWintDeviationDialog = useWintDeviationCreateDialog();
  const { buildSalaryChangeMenuItems } = useSalaryChangeActions();

  const onAddDeviationClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(displayDialog(createWintDeviationDialog(undefined, person, undefined, reloadHandler)));
  };

  const onError = (error: any) => {
    error &&
      dispatch(
        displayErrorMessage(
          LocalizationHelperInstance.getFormattedMessage(error?.message ?? TranslationKeys.Errors.SomethingWentWrong),
          {
            isGlobal: true,
          }
        )
      );
  };

  const yearMonth = DateHelper.getYearMonth(startDate);

  const moreActions: MoreActionMenuItem[] = [
    {
      description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.AddDeviation),
      icon: IconDefinitionConstants.WPlus,
      actionDialog: { ...createWintDeviationDialog(undefined, person, undefined, reloadHandler), onError: onError },
      hidden: hideMoreActions,
    },
    ...buildSalaryChangeMenuItems({
      personId: person.id,
      yearMonth: yearMonth,
      personName: person.name,
      onActionSuccess: () => reloadHandler?.(),
      hiddenZero: hideMoreActions,
      hiddenAdjust: hideMoreActions,
    }),
  ];

  return (
    // Wide: the saldo table, payout card and more-actions button sit on a single row,
    // vertically centred with equal horizontal space between them. Narrow: a container
    // query in _salary.scss stacks the saldo table and payout card vertically (per Figma)
    // once the footer is too narrow for them to fit side by side.
    <div className={cssExports.deviationRowFooterContent}>
      <div className={cssExports.deviationRowFooterItems}>
        {accumulators?.length ? (
          <div className={cssExports.deviationRowFooterSaldo}>
            <AdaptiveDetailTableView<AccumulatorsSummaryItem>
              data={accumulators}
              getRowChildren={(row) => row.children}
              properties={{
                description: {
                  header: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.SalaryDeviation.AccumulatorBalanceHeader
                  ),
                  width: 210,
                },
                change: {
                  header: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.SalaryDeviation.AccumulatorChangeHeader
                  ),
                  className: cssExports.BlackText,
                  noWrap: true,
                  alignment: "right",
                  width: 80,
                  formatter: (value: AccumulatorsSummaryItem) =>
                    LocalizationHelperInstance.getFormattedNumber(value.change ?? 0, 0, 2, "exceptZero"),
                },
                total: {
                  header: LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.SalaryDeviation.AccumulatorTotalHeader
                  ),
                  className: cssExports.BlackText,
                  noWrap: true,
                  alignment: "right",
                  width: 60,
                  formatter: (value: AccumulatorsSummaryItem) =>
                    LocalizationHelperInstance.getFormattedNumber(value.total ?? 0, 0, 2),
                },
              }}
            />
          </div>
        ) : null}
        {!hideUserPayoutDetails && (
          <div className={cssExports.deviationRowFooterPayout}>
            <UserPayoutDetails
              regularPayoutAmount={regularPayoutAmount}
              taxAmount={taxAmount}
              taxTable={taxTable}
              earlyPayoutsAmount={earlyPayoutsAmount}
              regularPayoutDate={regularPayoutDate}
              payouts={payouts}
              unscheduledPayoutLabel={unscheduledPayoutLabel}
            />
          </div>
        )}
        {!hideMoreActions && (
          <div className={cssExports.deviationRowFooterActions}>
            {actionType === "moreOptions" ? (
              <MenuButton
                menuItems={moreActions}
                menuPlacement="bottom-end"
                button={
                  <RoundButton size="sm" className={clsx(cssExports.iconButton, cssExports.rowFooter)}>
                    <Icon icon={IconDefinitionConstants.WEllipsisH} />
                  </RoundButton>
                }
                classes={{
                  menuOpen: cssExports.listMoreActionsMenuOpen,
                }}
              />
            ) : (
              <RoundButton
                size="sm"
                onClick={onAddDeviationClick}
                className={clsx(cssExports.iconButton, cssExports.rowFooter)}
              >
                <Icon icon={IconDefinitionConstants.WPlus} />
              </RoundButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WintDeviationRowFooter;
